/** toTitleCase function from https://stackoverflow.com/questions/196972/convert-string-to-title-case-with-javascript
 * @param {string} str - String to convert to title case
 * @returns {string} - String in title case
 * @example
 * toTitleCase('hello world'); // Hello World
 */
export const toTitleCase = (str) => {
	return str.replace(/\w\S*/g, (txt) => {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
};

/**
 * @param {number} number - Number to round
 * @param {number} decimalPlaces - Number of decimal places to round to
 * @returns {number} - Rounded number
 * @example
 * round(1.2345, 2); // 1.23
 */
export const round = (number, decimalPlaces = 2) => {
	if (typeof number !== 'number') {
		return 0;
	}
	return Number(number.toFixed(decimalPlaces));
};

/**
 * @param {object} obj - Object to sort
 * @returns {object} - Object with keys sorted alphabetically
 * @example
 * const obj = { b: 1, a: 2, c: 3 };
 * sortObject(obj); // { a: 2, b: 1, c: 3 }
 */
export const sortObject = (obj) => {
	return Object.keys(obj)
		.sort()
		.reduce((acc, key) => {
			acc[key] = obj[key];
			return acc;
		}, {});
};

/**
 * @param {URLSearchParams} searchParams - URL object to append queries to
 * @param {QueryObject} queryObj - Object of queries
 * @returns {string} - URL object with queries appended
 * @example
 * const url = new URL('http://localhost/reviews');
 * appendQueryArrayToUrl(url, 'ratings', [1, 2, 3, 4, 5]);
 * url.toString(); // http://localhost/reviews?ratings[]=1&ratings[]=2&ratings[]=3&ratings[]=4&ratings[]=5
 */
export const appendQueryToUrlSearchParams = (searchParams, queryObj) => {
	// re assembles object with keys sorted alphabetically
	// to ensure consistent url query string
	// makes sure that the cache is using the same key
	// https://stackoverflow.com/questions/5467129/sort-javascript-object-by-key
	const sortedQueryObj = sortObject(queryObj);
	Object.entries(sortedQueryObj).forEach(([key, value]) => {
		if (!value) {
			return;
		}
		if (Array.isArray(value)) {
			value.forEach((currValue) => {
				searchParams.append(`${key}[]`, currValue);
			});
		} else {
			searchParams.append(key, value);
		}
	});
};

/**
 * @param {string} path - Path to append to the host
 * @param {object} queries - Object of queries
 * @param {string} host - Host to append the path to
 * @returns {URl} - URL
 * @example
 * const url = buildUrl(
 * 	'/reviews',
 * 	{
 * 		models: ['Jetta'],
 * 		types: 'All'
 * 	},
 * 	window.location.href
 * );
 * console.log(url);
 * // http://localhost/reviews?models%5B%5D=Jetta&types=All
 */
export const buildUrl = (path, queries, host) => {
	const url = new URL(path, host);
	appendQueryToUrlSearchParams(url.searchParams, queries);
	return url;
};

/** merge two objects, two level deep
 * @param {object} obj1 - Object to merge into
 * @param {object} obj2 - Object to merge from
 * @returns {object} - Merged object
 * @example
 * const obj1 = { a: { b: 1 } };
 * const obj2 = { a: { b: 3, c: 2 } };
 * mergeObjects(obj1, obj2); // { a: { b: 3, c: 2 } }
 */
export const mergeObjects = (obj1, obj2) => {
	const merged = { ...obj1 };
	Object.keys(obj2).forEach((key) => {
		if (merged[key] && typeof merged[key] === 'object') {
			merged[key] = { ...merged[key], ...obj2[key] };
		} else {
			merged[key] = obj2[key];
		}
	});
	return merged;
};

/** get key value string
 * @param {object} obj - Object to get key value string from
 * @returns {string} - Key value string
 * @example
 * const obj = { a: 1, b: 2 };
 * getKeyValueString(obj); // a: 1, b: 2
 */
export const getKeyValueString = (obj) => {
	return Object.entries(obj)
		.map(([key, value]) => `${key}: ${value}`)
		.join(', ');
};

/** validate number
 * @param {number} number - Number to validate
 * @returns {boolean} - boolean
 * @example
 * const number = 4;
 * isValidNumber(number); // true
 */
export const isValidNumber = (number) => !Number.isNaN(Number(number));
